export type TextOutputDefinition = {
  key: string;
  label: string;
  filename: string;
  enabled: boolean;
  template: string;
  preview: string;
  description: string;
};

export type TextOutputsSnapshot = {
  settings: {
    enabled: boolean;
    outputDirectory: string;
    timeFormat: 'HH:mm' | 'HH:mm:ss';
    dateFormat: 'DD/MM/YYYY' | 'ddd DD/MM/YYYY' | 'D MMMM YYYY';
    viewerOfflineMode: 'zero' | 'blank' | 'retain';
    recentListLimit: number;
    sessionStartedAt: string;
  };
  definitions: TextOutputDefinition[];
  twitch: {
    configured?: boolean;
    connected?: boolean;
    broadcasterId?: string | null;
    broadcasterName?: string | null;
    currentViewers?: number | null;
    currentSubTotal?: number | null;
    followerTotal?: number | null;
    live?: boolean;
    streamTitle?: string | null;
    gameName?: string | null;
    streamStartedAt?: string | null;
    lastRefreshAt?: string | null;
    lastEventAt?: string | null;
    lastError?: string | null;
    [key: string]: unknown;
  };
  agent: {
    status: string;
    outputDirectory: string | null;
    writtenFiles: number;
    lastWriteAt: string | null;
    nowPlaying: string | null;
    lastError: string | null;
    updatedAt?: string;
  };
  activeAgentId: string | null;
  activeAgentName: string | null;
  activeAgentConnected: boolean;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(init?.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(body.message ?? body.error ?? `Request failed with HTTP ${response.status}.`);
  }
  return (await response.json().catch(() => ({}))) as T;
}

export function loadTextOutputs(): Promise<TextOutputsSnapshot> {
  return request('/api/v1/text-outputs');
}

export function updateTextOutputSettings(changes: Partial<TextOutputsSnapshot['settings']>): Promise<TextOutputsSnapshot> {
  return request('/api/v1/text-outputs/settings', {
    method: 'PUT',
    body: JSON.stringify(changes),
  });
}

export function updateTextOutput(key: string, changes: Partial<Pick<TextOutputDefinition, 'filename' | 'enabled' | 'template'>>): Promise<TextOutputsSnapshot> {
  return request(`/api/v1/text-outputs/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify(changes),
  });
}

export function refreshTextOutputs(): Promise<TextOutputsSnapshot> {
  return request('/api/v1/text-outputs/refresh', { method: 'POST' });
}

export function resetTextOutputSession(): Promise<TextOutputsSnapshot> {
  return request('/api/v1/text-outputs/reset-session', { method: 'POST' });
}

export function disconnectTwitchTextOutputs(): Promise<unknown> {
  return request('/api/v1/integrations/twitch/disconnect', { method: 'POST' });
}
